import { ReactNode } from "react";

/**
 * Flat side-view cars for the home page's "Browse by type" tiles (Taha, 7 Oct): one drawing per
 * kind of vehicle, picked from the class name the tenant typed, so every tile matches whatever
 * photos the fleet has (or hasn't). Drawn in the theme's own colours: the body in the text
 * colour, the glass and hubs in the card colour, the lights in the accent.
 */
export type CarKind = "sedan" | "suv" | "hatchback" | "convertible" | "sports" | "pickup" | "van";

// First match wins, so "Sport SUV" is an SUV and "Minivan" a van; anything else is a sedan.
const KIND_RULES: [RegExp, CarKind][] = [
  [/\b(suvs?|crossovers?|4x4|4wd|awd|jeeps?|off-?road)\b/i, "suv"],
  [/\b(convertibles?|cabrio(let)?s?|roadsters?|spyders?|spiders?)\b/i, "convertible"],
  [/\b(pick-?ups?|trucks?|utes?)\b/i, "pickup"],
  [/\b(vans?|minivans?|mpvs?|bus(es)?|people carriers?|passenger|\d+[- ]?seaters?)\b/i, "van"],
  [/\b(sports?|coupes?|coupés?|supercars?|exotics?|muscle)\b/i, "sports"],
  [/\b(hatch(back)?s?|compacts?|economy|minis?|small|city)\b/i, "hatchback"],
];

export const carKind = (className: string): CarKind =>
  KIND_RULES.find(([pattern]) => pattern.test(className))?.[1] ?? "sedan";

interface Art {
  body: string;
  glass?: string;
  /** [cx, radius] of the rear and front wheels. */
  wheels: [number, number][];
  /** Head light [x, y] and tail light [x, y, height]. */
  head: [number, number];
  tail: [number, number, number];
  extra?: ReactNode;
}

const INK = "var(--db-ink)";

const ART: Record<CarKind, Art> = {
  sedan: {
    body: "M30 80 L28 66 L40 60 L66 56 L90 38 L142 37 L168 55 L206 61 L213 70 L212 80 Z",
    glass: "M74 56 L94 43 L114 42 L114 56 Z M119 56 L119 42 L140 42 L160 56 Z",
    wheels: [[64, 16], [178, 16]],
    head: [201, 63],
    tail: [27, 63, 6],
  },
  suv: {
    body: "M28 80 L27 44 L42 28 L150 28 L172 50 L206 56 L213 66 L212 80 Z",
    glass: "M36 50 L46 34 L80 34 L80 50 Z M86 50 L86 34 L120 34 L120 50 Z M126 50 L126 34 L148 34 L164 50 Z",
    wheels: [[64, 18], [180, 18]],
    head: [201, 59],
    tail: [26, 54, 9],
  },
  hatchback: {
    body: "M44 80 L42 58 L58 38 L130 37 L156 55 L194 61 L201 70 L200 80 Z",
    glass: "M52 56 L64 43 L96 42 L96 56 Z M101 56 L101 42 L128 42 L148 56 Z",
    wheels: [[76, 16], [168, 16]],
    head: [189, 63],
    tail: [41, 60, 8],
  },
  convertible: {
    body: "M30 80 L28 66 L40 60 L148 58 L206 61 L213 70 L212 80 Z",
    wheels: [[64, 16], [178, 16]],
    head: [201, 63],
    tail: [27, 63, 6],
    extra: (
      <>
        <path d="M140 58 L152 42" stroke={INK} strokeWidth="5" strokeLinecap="round" />
        <path d="M86 58 C86 50 98 48 102 54 L103 58 Z" fill={INK} stroke={INK} strokeWidth="6" strokeLinejoin="round" />
      </>
    ),
  },
  sports: {
    body: "M30 80 L28 70 L44 62 L82 46 C104 40 126 40 146 47 L172 59 L210 64 L216 72 L214 80 Z",
    glass: "M66 60 L90 50 C104 46 116 46 128 48 L128 60 Z M132 60 L132 49 C140 51 146 54 156 60 Z",
    wheels: [[66, 15], [184, 15]],
    head: [205, 66],
    tail: [27, 66, 5],
  },
  pickup: {
    body: "M26 80 L25 58 L102 58 L104 30 L150 30 L172 52 L206 56 L213 66 L212 80 Z",
    glass: "M110 52 L110 36 L146 36 L162 52 Z",
    wheels: [[62, 18], [182, 18]],
    head: [201, 59],
    tail: [24, 60, 9],
    extra: <rect x="29" y="62" width="70" height="2.5" rx="1" fill="var(--db-card)" opacity="0.3" />,
  },
  van: {
    body: "M24 80 L23 30 L34 22 L156 22 L188 48 L208 54 L214 64 L213 80 Z",
    glass: "M32 46 L32 30 L72 30 L72 46 Z M78 46 L78 30 L118 30 L118 46 Z M124 46 L124 30 L152 30 L174 46 Z",
    wheels: [[60, 17], [182, 17]],
    head: [203, 57],
    tail: [22, 40, 14],
  },
};

/**
 * One car on the tile's accent band. The wheel arches are cut by painting the band's own two
 * layers (card colour, then the accent tint) behind each wheel, so they match the band exactly.
 */
export const CarArt = ({ kind, className }: { kind: CarKind; className?: string }) => {
  const art = ART[kind];
  return (
    <svg viewBox="0 0 240 112" className={className} aria-hidden="true" focusable="false">
      <ellipse cx="120" cy="104" rx="98" ry="4" fill={INK} opacity="0.1" />
      <path d={art.body} fill={INK} stroke={INK} strokeWidth="6" strokeLinejoin="round" />
      {art.glass && <path d={art.glass} fill="var(--db-card)" opacity="0.9" />}
      {art.extra}
      <rect x={art.head[0]} y={art.head[1]} width="10" height="5" rx="2.5" fill="var(--db-accent)" />
      <rect x={art.tail[0]} y={art.tail[1]} width="5" height={art.tail[2]} rx="2" fill="var(--db-accent)" />
      {art.wheels.map(([cx, r]) => (
        <g key={cx}>
          <circle cx={cx} cy="84" r={r + 4} fill="var(--db-card)" />
          <circle cx={cx} cy="84" r={r + 4} fill="var(--db-accent-tint)" />
          <circle cx={cx} cy="84" r={r} fill={INK} />
          <circle cx={cx} cy="84" r={r / 2} fill="var(--db-card)" opacity="0.85" />
        </g>
      ))}
    </svg>
  );
};
